import { spellsFor, type Spell, type Vocation } from '@game/protocol';

import { isTyping } from '../input/focus.ts';
import { collapsiblePanel } from './panel.ts';

/**
 * Barra de hotkeys de magia (PRD §6).
 *
 * As magias vêm do catálogo compartilhado, filtradas pela vocação: não existe pacote de
 * "suas magias", porque a lista é derivável de vocação e nível — dois números que o
 * cliente já tem. Um pacote a mais aqui seria estado duplicado esperando divergir.
 *
 * O que o cliente **não** decide é se a conjuração vale: ele mostra a magia apagada
 * quando falta nível ou mana, manda mesmo assim se o jogador insistir, e o servidor
 * recusa com uma mensagem. A tela apagada é conveniência, não regra.
 *
 * O código contradizia esse contrato: falta de nível era cortada **aqui**, antes de
 * mandar, e o jogador apertava a tecla sem receber resposta nenhuma — nem mensagem, nem
 * mana gasta, nem nada no chat (PRD §11.2, item 6). Falta de mana, que passa pelo
 * servidor, sempre avisou. Agora as duas passam, e a mensagem sai do mesmo lugar.
 */

export interface HotkeyHandlers {
  onCast(spell: Spell): void;
}

interface Slot {
  spell: Spell;
  root: HTMLElement;
  cooldown: HTMLElement;
  /** Fim da recarga local, em ms de `performance.now()`. */
  readyAt: number;
}

/** Teclas da barra, na ordem. Nove magias cobrem o catálogo de qualquer vocação. */
const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9'];

export class HotkeyBar {
  private readonly root: HTMLElement;
  /** Onde os botões moram. Ver `setVocation`: a barra de título não pode ser varrida junto. */
  private readonly row: HTMLElement;
  private readonly handlers: HotkeyHandlers;
  private slots: Slot[] = [];

  private level = 1;
  private mana = 0;

  constructor(root: HTMLElement, handlers: HotkeyHandlers) {
    this.root = root;
    this.handlers = handlers;
    this.root.classList.add('hotkeys');

    // Recolher a barra é seguro de um jeito que recolher o chat não era: as teclas 1 a 9
    // continuam conjurando com ela escondida, porque quem as escuta é o `window` e não o
    // botão. O que some é o lembrete de qual magia é qual — que é justamente o que sobra
    // depois da vigésima caçada.
    const header = collapsiblePanel(this.root, 'magias', 'Magias');

    this.row = document.createElement('div');
    this.row.className = 'hotkeys-row';
    this.root.append(header.root, this.row);

    window.addEventListener('keydown', (event) => {
      // Ignora quando o foco está num campo de texto: digitar "3" no chat não pode
      // disparar uma bola de fogo.
      if (isTyping(event.target)) return;

      // A repetição automática do sistema operacional dispara ~30 `keydown` por segundo
      // com a tecla presa. Sem esta guarda, segurar uma hotkey somaria trinta frames de
      // Cast por segundo ao tráfego de movimento e derrubaria a conexão pelo mesmo
      // mecanismo do item 1 de §11.1. Uma conjuração é um gesto, não um estado.
      if (event.repeat) return;

      const index = KEYS.indexOf(event.key);
      if (index === -1) return;

      const slot = this.slots[index];
      if (!slot) return;

      event.preventDefault();
      this.trigger(slot);
    });
  }

  /** Monta a barra com as magias da vocação escolhida. Chamado uma vez, no Welcome. */
  setVocation(vocation: Vocation): void {
    // A fileira, e não a raiz: `this.root.innerHTML = ''` levaria a barra de título junto, e
    // a barra some para sempre porque este método só roda uma vez, no `Welcome`.
    this.row.innerHTML = '';
    this.slots = [];

    spellsFor(vocation)
      .slice(0, KEYS.length)
      .forEach((spell, index) => {
        const button = document.createElement('button');
        button.className = 'hotkey';
        button.type = 'button';

        const key = document.createElement('span');
        key.className = 'hotkey-key';
        key.textContent = KEYS[index] ?? '';

        const name = document.createElement('span');
        name.className = 'hotkey-name';
        name.textContent = spell.name;

        const cost = document.createElement('span');
        cost.className = 'hotkey-cost';
        cost.textContent = `${spell.manaCost} mana`;

        const cooldown = document.createElement('div');
        cooldown.className = 'hotkey-cooldown';

        button.append(key, name, cost, cooldown);
        button.title = `${spell.name} — "${spell.words}" · nível ${spell.minLevel} · ${spell.manaCost} de mana`;

        const slot: Slot = { spell, root: button, cooldown, readyAt: 0 };
        button.addEventListener('click', () => this.trigger(slot));

        this.row.append(button);
        this.slots.push(slot);
      });

    this.refresh();
  }

  /** Nível e mana atuais, para apagar o que não dá para conjurar. */
  setStatus(level: number, mana: number): void {
    this.level = level;
    this.mana = mana;
    this.refresh();
  }

  /** Anima a recarga. Chamado a cada quadro pela cena. */
  update(): void {
    const now = performance.now();

    for (const slot of this.slots) {
      const remaining = slot.readyAt - now;
      if (remaining <= 0) {
        slot.cooldown.style.height = '0%';
        continue;
      }

      const percent = (remaining / slot.spell.cooldownMs) * 100;
      slot.cooldown.style.height = `${Math.min(100, percent)}%`;
    }
  }

  private trigger(slot: Slot): void {
    if (performance.now() < slot.readyAt) return;

    // Nível insuficiente **manda mesmo assim**: quem responde é o servidor, e a resposta
    // dele — "Essa magia exige nível 8." — é a única forma de o jogador descobrir o
    // requisito sem ler o `title` do botão no meio de uma briga.
    //
    // O que não acontece é armar a recarga otimista: a conjuração que se sabe recusada
    // não gastou recarga nenhuma, e apagar o botão por oito segundos por causa dela
    // esconderia a magia justamente de quem acabou de subir de nível.
    const locked = this.level < slot.spell.minLevel;

    if (!locked) {
      // A recarga local é otimista: começa a contar antes da confirmação do servidor.
      // Se ele recusar, a mensagem explica o motivo e a barra volta no tempo previsto —
      // o que é preferível a piscar "pronta" enquanto o servidor ainda não deixou.
      slot.readyAt = performance.now() + slot.spell.cooldownMs;
    }

    this.handlers.onCast(slot.spell);
    this.refresh();
  }

  private refresh(): void {
    for (const slot of this.slots) {
      const locked = this.level < slot.spell.minLevel;
      const broke = this.mana < slot.spell.manaCost;

      slot.root.classList.toggle('locked', locked);
      slot.root.classList.toggle('broke', !locked && broke);
    }
  }
}
